import * as React from 'react';
import { css } from '@emotion/core';
import { COLORS } from '../globalStyle/colors';
import LocalDiv from '../localization/LocalDiv';
import { FLEX_COLUMN, FLEX_ROW, FLEX_CENTERED } from '../layout/flexBox';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { faCircle as faCircleSolid, faCheck } from '@fortawesome/free-solid-svg-icons';
import Spacer from '../layout/Spacer';
import { RaterApprovalRater } from './models';
import ActionIcon from '../layout/ActionIcon';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import { doNothing } from '../language/module';
import { TextDirection } from '../localization/textDirection';
import { lastCommaFirstLocal } from '../project/survey/raters/module';


const styles = {
  root:       [
    FLEX_COLUMN,
    css`
      margin-top: 10px;
      padding: 18px;
    `,
  ],
  infoRow:    [
    FLEX_ROW,
    css`
    display: flex;
    flex-direction: row;
    `,
  ],
  label:      css`font-weight: bold;`,
  iconRow:    [
    FLEX_ROW,
    css`
    width: 100%;
    justify-content: flex-start;
    margin-top: 18px;
  `,
  ],
  actionIcon: {
    root:        [
      FLEX_COLUMN,
      FLEX_CENTERED,
      css`
        font-size: 18px;
        color: ${COLORS.ICON_GREY};
        cursor: pointer;
      `,
    ],
    icon:        css`
      font-size: 18px;
      margin-bottom: 8px;
    `,
    caption:     css`font-weight: bold; font-size: 12px;`,
    green:       css`color: ${COLORS.GREEN};`,
    gray:        css`color: ${COLORS.DARK_GREY};`,
    translucent: css`opacity: 0.4;`,
  },
};


type Props = {
  className?: string;
  rater: RaterApprovalRater;
  textDirection: TextDirection;

  showName: boolean;
  approved: boolean;
  declined: boolean;
  canEdit: boolean;

  markRaterApproved: () => void;
  markRaterDeclined: () => void;
};


/**
 * RaterApprovalCard: a component that presents information about a yet-to-be-approved project rater, as a card
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterApprovalCard: React.FC<Props> = props => {
  const { markRaterApproved, markRaterDeclined, ...rest } = props;

  const onClickApproveIcon = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      markRaterApproved();
    },
    [markRaterApproved],
  );

  const onClickDeclineIcon = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      markRaterDeclined();
    },
    [markRaterDeclined],
  );

  const approveIconProps = React.useMemo(
    () => rest.canEdit
      ? {
        icon:       rest.approved ? faCircleSolid : faCircle,
        onClick:    onClickApproveIcon,
        captionMsg: Str.Approve,
        css:        rest.approved ? styles.actionIcon.green : styles.actionIcon.gray,
      }
      : {
        icon:       faCheck,
        onClick:    doNothing,
        captionMsg: Str.ApprovalStatus3,
        css:        [
          rest.approved
            ? styles.actionIcon.green
            : [styles.actionIcon.gray, styles.actionIcon.translucent],
        ],
      },
    [onClickApproveIcon, rest.approved, rest.canEdit],
  );

  const declineIconProps = React.useMemo(
    () => rest.canEdit
      ? {
        icon:       rest.declined ? faCircleSolid : faCircle,
        onClick:    onClickDeclineIcon,
        captionMsg: Str.Decline,
        css:        undefined,
      }
      : {
        icon:       faCheck,
        onClick:    doNothing,
        captionMsg: Str.ApprovalStatus4,
        css:        !rest.declined && styles.actionIcon.translucent,
      },
    [onClickDeclineIcon, rest.canEdit, rest.declined],
  );

  return (
    <LocalDiv css={styles.root} className={rest.className}>

      {
        props.showName && (
          <>
            <LocalDiv css={styles.infoRow}>
              <Msg str={Str.NameColon} css={styles.label} />
              <Spacer sizePx={8} />
              <Msg str='' defaultMsg={lastCommaFirstLocal(rest.textDirection, rest.rater)} />
            </LocalDiv>

            <Spacer sizePx={8} />
          </>
        )
      }

      <LocalDiv css={styles.infoRow}>
        <Msg str={Str.EmailColon} css={styles.label} />
        <Spacer sizePx={8} />
        <Msg str='' defaultMsg={rest.rater.email} />
      </LocalDiv>

      <Spacer sizePx={8} />

      <LocalDiv css={styles.infoRow}>
        <Msg str={Str.RelationshipColon} css={styles.label} />
        <Spacer sizePx={8} />
        <Msg str='' defaultMsg={rest.rater.levelName} />
      </LocalDiv>

      <LocalDiv css={styles.iconRow}>
        <ActionIcon
          icon={approveIconProps.icon}
          iconStyle={styles.actionIcon.icon}
          onClick={approveIconProps.onClick}
          caption={{ str: approveIconProps.captionMsg, captionStyle: styles.actionIcon.caption }}
          css={[styles.actionIcon.root, approveIconProps.css]}
        />

        <Spacer sizePx={24} />

        <ActionIcon
          icon={declineIconProps.icon}
          iconStyle={styles.actionIcon.icon}
          onClick={declineIconProps.onClick}
          caption={{ str: declineIconProps.captionMsg, captionStyle: styles.actionIcon.caption }}
          css={[styles.actionIcon.root, styles.actionIcon.gray, declineIconProps.css]}
        />
      </LocalDiv>

    </LocalDiv>
  );
};

export default RaterApprovalCard;
